import { Trans, useLingui } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import {
  patientsWithUncancelledHomeVisitsQueryOptions,
  useCareHub,
} from "@/api/Patients";
import type {
  CircleLayerSpecification,
  SymbolLayerSpecification,
  LineLayerSpecification,
} from "mapbox-gl";
import Map from "@/components/Map/Map";
import { useCallback, useEffect, useMemo } from "react";
import { Layer, Source, useMap } from "react-map-gl/mapbox";
import type { GeoJSONSource, MapMouseEvent } from "mapbox-gl";
import { getVisitsCoordinates, useRoutes } from "@/api/Routes";
import { shiftsWithContentsQueryOptions } from "@/api/Shifts";
import { useUnplannedOccurrencesAndGroups } from "@/api/Activities";
import { useQuery } from "@tanstack/react-query";
import { useVehicles } from "@/api/Vehicles";
import { useSelectedDate } from "@/Utils/useSelectedDate";
import { useDirections } from "@/api/Mapbox";
import { UnstyledSingleSelectToggleButtonGroup } from "@medoma/components/ToggleButton/SingleSelectToggleButtonGroup";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import type { ISelectedItem } from "./Selection/SelectionContext";
import {
  selectedItemTypeSchema,
  useSelection,
} from "./Selection/SelectionContext";
import { patientStatusSchema } from "@medoma/models/patients";
import Address from "@/components/Address/Address";
import { useUserProfile } from "@/api/Users";
import styles from "./PlanningMap.module.scss";
import { useTimeFilter } from "./useTimeFilter";
import {
  filterRoutesByTimeWindow,
  occurrencesOverlapTimeWindow,
} from "@/api/timeWindow";
import { buildPatientFeatures } from "./buildPatientFeatures";
import { findFirstVisitForPatient } from "./findFirstVisitForPatient";
import { filterPatientsByTeam } from "./filterPatientsByTeam";
import { useTeamFilter } from "./useTeamFilter";
import PanelRightIcon from "@medoma/components/icons/PanelRightIcon";
import PanelBottomIcon from "@medoma/components/icons/PanelBottomIcon";
import {
  mapPositionSchema,
  usePlanningViewToolbar,
} from "./usePlanningViewToolbar";
import type { IShiftWithContents } from "@medoma/models/shiftWithContents";
import type { IUnplannedOccurrenceOrGroup } from "@medoma/models/activities";

const formatter = new Intl.ListFormat(navigator.language, {
  style: "long",
  type: "disjunction",
});

const patientsSourceId = "patients";
const patientClusterLayerId = "patient-clusters";
const patientUnclusteredLayerId = "patient-unclustered-point";

// Local shape for the properties we read off patient-layer features. The
// mapbox-gl typedefs expose `GeoJSONFeature` but the transitive `GeoJSON.Feature`
// (from @types/geojson) isn't installed in this repo, so `geometry`/`properties`
// come out untyped. We cast query/event results to this to get back inline
// access without an `as unknown as ...` at each callsite.
type PatientLayerFeature = {
  layer?: { id: string };
  geometry: { type: string; coordinates: [number, number] };
  properties: {
    cluster_id?: number;
    patient_id?: string;
    has_home_visit?: boolean;
  } | null;
};

const lineLayer: LineLayerSpecification = {
  source:
    "String needed. Check this issue: https://github.com/visgl/react-map-gl/issues/2411",
  id: "route-visualization",
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "color"],
    "line-opacity": ["get", "opacity"],
    "line-width": 4,
  },
};

const symbolLayer: SymbolLayerSpecification = {
  source:
    "String needed. Check this issue: https://github.com/visgl/react-map-gl/issues/2411",
  id: "route-labels",
  type: "symbol",
  layout: {
    "text-field": ["get", "name"],
    "text-size": ["get", "size"],
    "text-allow-overlap": true,
  },
  paint: {
    "text-color": "#fff",
    "text-halo-color": "#fff",
    "text-halo-width": 0.5,
  },
};

// Since we do not know beforehand which points will be clustered by mapbox, we need to use mapbox syntax instead of js to show different cases
const clusterLayer = (
  entity: "patient" | "vehicle",
): CircleLayerSpecification => {
  return {
    source:
      "String needed. Check this issue: https://github.com/visgl/react-map-gl/issues/2411",
    id: `${entity}-clusters`,
    type: "circle",
    filter: ["has", "point_count"],
    paint: {
      "circle-color":
        entity === "patient"
          ? [
              "case",
              ["!=", ["get", "examined_patient_name"], null],
              "#00328a", // blue-40 for clusters containing the selected patient
              ["==", ["get", "in_selected_route"], true],
              "#186ce9", // blue-50 for clusters containing patients in the selected route
              ["==", ["get", "has_home_visit"], false],
              "#a3a3a3", // gray-70 for patient clusters without home visits
              ["==", ["get", "point_count"], 2],
              "#0d8f2a", // green-50 for clusters with 2 patients
              "#047003", // green-40 for clusters with more than 2 patients
            ]
          : "#FF8921", // orange-60 for vehicle clusters
      "circle-radius":
        entity === "patient"
          ? [
              "case",
              ["!=", ["get", "examined_patient_name"], null],
              15, // cluster including selected patient
              ["==", ["get", "in_selected_route"], true],
              12, // cluster containing patients in selected route
              ["==", ["get", "has_home_visit"], true],
              10, // patient cluster with home visit
              5, // patient cluster without home visits
            ]
          : 10, // vehicle cluster
      "circle-stroke-width": 2,
      "circle-stroke-color":
        entity === "patient"
          ? [
              "case",
              ["!=", ["get", "examined_patient_name"], null],
              "#042662", // darkest blue stroke for cluster including selected patient
              ["==", ["get", "in_selected_route"], true],
              "#1f36a2", // dark blue stroke for clusters with patients in selected route
              ["==", ["get", "has_home_visit"], true],
              "#045C03", // darker green stroke for clusters with home visit
              "#757575", // gray-50 stroke for clusters without home visits
            ]
          : "#C15B00", // orange-40 stroke for vehicles
      "circle-opacity": 0.8,
    },
  };
};

const circleNameLayer = (
  entity: "patient" | "vehicle",
): SymbolLayerSpecification => {
  return {
    source:
      "String needed. Check this issue: https://github.com/visgl/react-map-gl/issues/2411",
    id: `${entity}-cluster-count`,
    type: "symbol",
    layout: {
      // If there is a `name` field, use it, otherwise use the `point_count` field (e.g. 2) and append " bilar" or " patienter"
      "text-field": [
        "coalesce",
        ["get", "name"],
        [
          "case",
          ["!=", ["get", "examined_patient_name"], null],
          [
            "concat",
            ["get", "examined_patient_name"],
            " + ",
            ["to-string", ["-", ["get", "point_count"], 1]],
            entity === "vehicle" ? " " + t`bilar` : " " + t`patienter`,
          ],
          [
            "concat",
            ["get", "point_count"],
            entity === "vehicle" ? " " + t`bilar` : " " + t`patienter`,
          ],
        ],
      ],
      // -1.5 puts the label over the circle instead of on top of it. Dependent on "circle-radius": 10,
      "text-offset": [0, -1.5],
      "text-size": 12,
      "text-allow-overlap": true,
    },
    paint: {
      "text-halo-color": "#fff",
      "text-halo-width": 1.5,
    },
  };
};

// Since we already use mapbox case syntax for clustered layer, it would be confusing if we used js values (i.e. ["get", "color"]) here.
// It would be high likelihood that we would later think that modifying js values would also change clustered layout, which would not be the case.
// Therefore we choose mapbox syntax also here.
const unclusteredLayer = (
  entity: "patient" | "vehicle",
): CircleLayerSpecification => {
  return {
    source:
      "String needed. Check this issue: https://github.com/visgl/react-map-gl/issues/2411",
    id: `${entity}-unclustered-point`,
    type: "circle",
    // Only show unclustered points when not part of a cluster
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-color":
        entity === "vehicle"
          ? "#FF8921" // orange-60 for vehicles
          : [
              "case",
              ["!=", ["get", "examined_patient_name"], null],
              "#00328a", // blue-40 for selected patient
              ["==", ["get", "in_selected_route"], true],
              "#186ce9", // blue-50 for patients in the selected route
              ["==", ["get", "has_home_visit"], true],
              "#1aa339", // green-60 for patients with home visit
              "#a3a3a3", // gray-50 for patients without home visit
            ],
      "circle-radius":
        entity === "patient"
          ? [
              "case",
              ["!=", ["get", "examined_patient_name"], null],
              15, // patient is selected
              ["==", ["get", "in_selected_route"], true],
              12, // patient is in selected route
              ["==", ["get", "has_home_visit"], true],
              10, // patient has home visit
              5, // patient without home visit
            ]
          : 10, // vehicles
      "circle-stroke-width": 2,
      "circle-stroke-color":
        entity === "patient"
          ? [
              "case",
              ["!=", ["get", "examined_patient_name"], null],
              "#042662", // darkest blue for selected patient
              ["==", ["get", "in_selected_route"], true],
              "#1f36a2", // dark blue for patients in selected route
              ["==", ["get", "has_home_visit"], true],
              "#045C03", // darker green for patients with home visit
              "#757575", // gray-50 for patients without home visit
            ]
          : "#C15B00", // orange-40 for vehicles
      "circle-opacity": 0.8,
    },
  };
};

const zoomIntoCluster = (
  event: MapMouseEvent,
  feature: PatientLayerFeature,
) => {
  const clusterId = feature.properties?.cluster_id;
  if (typeof clusterId !== "number") {
    return;
  }
  const [lng, lat] = feature.geometry.coordinates;
  const source = event.target.getSource(patientsSourceId) as
    | GeoJSONSource
    | undefined;
  if (!source) {
    return;
  }
  source.getClusterExpansionZoom(clusterId, (error, zoom) => {
    if (error || zoom === null || zoom === undefined) {
      return;
    }
    event.target.easeTo({ center: [lng, lat], zoom, duration: 500 });
  });
};

const unplannedSelectionFor = (
  patientId: string,
  unplannedItems: IUnplannedOccurrenceOrGroup[],
): ISelectedItem | null => {
  const match = unplannedItems.find((item) => item.patient?.id === patientId);
  return match
    ? { type: selectedItemTypeSchema.enum.itemToPlan, item: match }
    : null;
};

const plannedVisitSelectionFor = (
  patientId: string,
  shifts: IShiftWithContents[],
): ISelectedItem | null => {
  const firstVisit = findFirstVisitForPatient({ shifts, patientId });
  return firstVisit
    ? {
        type: selectedItemTypeSchema.enum.plannedVisit,
        item: firstVisit.visit,
        routeId: firstVisit.routeId,
        shiftId: firstVisit.shiftId,
      }
    : null;
};

export const PlanningMap = () => {
  const { t } = useLingui();
  const selectedDate = useSelectedDate();
  const timeFilter = useTimeFilter();
  const {
    selectedItem,
    setSelectedItem,
    boundsShowingAllPatients,
    setBoundsShowingAllPatients,
  } = useSelection();
  const { countryCode } = useUserProfile();

  const { mapPosition, setMapPosition } = usePlanningViewToolbar();
  const { planningMap } = useMap();

  const { data: patientsWithHomeVisitFlag, isError } = useQuery({
    ...patientsWithUncancelledHomeVisitsQueryOptions({
      fromDate: selectedDate,
      toDate: timeFilter?.endTime ?? selectedDate,
    }),
    // The backend only filters home visits on date level. Narrow to the exact
    // time-of-day window on the client. Done in `select` so changing the time
    // filter re-derives from cached data instead of triggering a refetch.
    select: (patients) =>
      patients.map(({ homeVisitOccurrences, ...patient }) => {
        const occurrencesInWindow = timeFilter
          ? homeVisitOccurrences.filter((occurrence) =>
              occurrencesOverlapTimeWindow([occurrence], timeFilter),
            )
          : homeVisitOccurrences;
        return {
          ...patient,
          hasHomeVisit: occurrencesInWindow.length > 0,
          requiredCompetences: [
            ...new Set(
              occurrencesInWindow.flatMap(
                (occurrence) => occurrence.requiredCompetences,
              ),
            ),
          ],
        };
      }),
  });

  const { selected: selectedTeams, allSelected: allTeamsSelected } =
    useTeamFilter();

  const filteredPatients = useMemo(() => {
    if (!patientsWithHomeVisitFlag) {
      return undefined;
    }
    return filterPatientsByTeam({
      patients: patientsWithHomeVisitFlag,
      selectedTeams,
      allTeamsSelected,
    });
  }, [patientsWithHomeVisitFlag, selectedTeams, allTeamsSelected]);

  const { data: vehicles = [], isError: isErrorVehicles } = useVehicles();

  const { data: routes = [], isError: isErrorRoutes } = useRoutes({
    from: selectedDate,
    to: timeFilter?.endTime ?? selectedDate,
  });

  const { data: shiftsWithContents } = useQuery(
    shiftsWithContentsQueryOptions(
      timeFilter
        ? {
            selectedDate,
            startTime: timeFilter.startTime,
            endTime: timeFilter.endTime,
          }
        : { selectedDate },
    ),
  );

  const { data: unplannedOccurrencesAndGroups } =
    useUnplannedOccurrencesAndGroups({
      fromDate: selectedDate,
      toDate: timeFilter?.endTime ?? selectedDate,
    });

  const timeFilteredRoutes = useMemo(
    () => filterRoutesByTimeWindow(routes, timeFilter),
    [routes, timeFilter],
  );

  const selectedRouteId =
    selectedItem &&
    (selectedItem.type === selectedItemTypeSchema.enum.plannedRoute ||
      selectedItem.type === selectedItemTypeSchema.enum.plannedVisit)
      ? selectedItem.routeId
      : null;

  const selectedRouteCoordinates = useMemo(() => {
    if (!selectedRouteId) {
      return [];
    }
    const selectedRoute = timeFilteredRoutes.find(
      ({ id }) => id === selectedRouteId,
    );
    return selectedRoute ? getVisitsCoordinates(selectedRoute.visits) : [];
  }, [timeFilteredRoutes, selectedRouteId]);

  const { data: directions } = useDirections(selectedRouteCoordinates);
  const { data: careHub } = useCareHub();

  const patientCoordinates = useMemo(() => {
    if (!filteredPatients || !careHub) {
      return undefined;
    }
    // Match the dots rendered by buildPatientFeatures: discharged patients
    // don't get a visible dot, so they shouldn't influence the initial bounds.
    const patientsWithDots = filteredPatients.filter(
      (patient) => patient.status !== patientStatusSchema.enum.discharged,
    );
    return [
      ...patientsWithDots.map((patient) => patient.address.coordinates),
      { longitude: careHub.longitude, latitude: careHub.latitude },
    ];
  }, [filteredPatients, careHub]);

  useEffect(() => {
    if (!patientCoordinates) {
      return;
    }
    setBoundsShowingAllPatients([
      {
        lat: Math.min(...patientCoordinates.map(({ latitude }) => latitude)),
        lng: Math.min(...patientCoordinates.map(({ longitude }) => longitude)),
      },
      {
        lat: Math.max(...patientCoordinates.map(({ latitude }) => latitude)),
        lng: Math.max(...patientCoordinates.map(({ longitude }) => longitude)),
      },
    ] as const);
  }, [patientCoordinates, setBoundsShowingAllPatients]);

  // A route spans many points, so its bounds give a useful framing when the map
  // first mounts. We don't do the same for other selection types (visits,
  // activities) since they're single points and would zoom in uncomfortably
  // close. Follow-up selections while the map is already shown go through the
  // imperative fitBounds in useSelectionInTimeline.
  const initialAreaToShow = useMemo(() => {
    const routeIsSelected =
      selectedItem?.type === selectedItemTypeSchema.enum.plannedRoute;
    if (routeIsSelected && careHub && selectedRouteCoordinates.length > 0) {
      const lngs = [
        careHub.longitude,
        ...selectedRouteCoordinates.map(({ longitude }) => longitude),
      ];
      const lats = [
        careHub.latitude,
        ...selectedRouteCoordinates.map(({ latitude }) => latitude),
      ];
      return {
        bounds: [
          { lng: Math.min(...lngs), lat: Math.min(...lats) },
          { lng: Math.max(...lngs), lat: Math.max(...lats) },
        ] as const,
      };
    }
    return boundsShowingAllPatients
      ? { bounds: boundsShowingAllPatients }
      : null;
  }, [
    selectedItem,
    careHub,
    selectedRouteCoordinates,
    boundsShowingAllPatients,
  ]);

  useEffect(() => {
    if (!planningMap) {
      return;
    }
    const map = planningMap;
    const canvas = map.getCanvas();

    const showPointerForPatientWithVisit = (event: MapMouseEvent) => {
      const feature = event.features?.[0] as PatientLayerFeature | undefined;
      if (feature?.properties?.has_home_visit) {
        canvas.style.cursor = "pointer";
      }
    };
    const resetCursor = () => {
      canvas.style.cursor = "";
    };

    const patientLayers = [patientClusterLayerId, patientUnclusteredLayerId];
    for (const layer of patientLayers) {
      map.on("mouseenter", layer, showPointerForPatientWithVisit);
      map.on("mouseleave", layer, resetCursor);
    }
    return () => {
      for (const layer of patientLayers) {
        map.off("mouseenter", layer, showPointerForPatientWithVisit);
        map.off("mouseleave", layer, resetCursor);
      }
    };
  }, [planningMap]);

  const handlePatientLayerClick = useCallback(
    (event: MapMouseEvent) => {
      const feature = event.features?.[0] as PatientLayerFeature | undefined;
      if (!feature || feature.geometry.type !== "Point") {
        return;
      }

      if (feature.layer?.id === patientClusterLayerId) {
        zoomIntoCluster(event, feature);
        return;
      }

      const patientId = feature.properties?.patient_id;
      if (typeof patientId !== "string") {
        return;
      }

      // Priority when a patient is clicked:
      //   1. Unplanned occurrence — needs the planner's attention
      //   2. Planned visit inside the current time window — what's visible in the panel
      //   3. Planned visit in a before-window shift
      //   4. Planned visit in an after-window shift
      // A patient can have several visits the same day; take the first match.
      const selection =
        unplannedSelectionFor(patientId, unplannedOccurrencesAndGroups ?? []) ??
        plannedVisitSelectionFor(
          patientId,
          shiftsWithContents?.relevant ?? [],
        ) ??
        plannedVisitSelectionFor(patientId, shiftsWithContents?.before ?? []) ??
        plannedVisitSelectionFor(patientId, shiftsWithContents?.after ?? []);
      if (selection) {
        setSelectedItem(selection);
      }
    },
    [shiftsWithContents, unplannedOccurrencesAndGroups, setSelectedItem],
  );

  const formattedErrors = formatter.format(
    [
      isError ? t`patienterna` : "",
      isErrorRoutes ? t`rutterna` : "",
      isErrorVehicles ? t`bilarna` : "",
    ].filter((errorMessage) => errorMessage.length > 0),
  );
  const customErrorMessage = [isError, isErrorRoutes, isErrorVehicles].some(
    (error) => error,
  )
    ? t`Kunde inte visa ${formattedErrors} i kartan. Försök att ladda om sidan, och kontakta teknisk support om det ändå inte fungerar.`
    : null;

  const selectedPatient =
    selectedItem &&
    selectedItem.type !== selectedItemTypeSchema.enum.plannedRoute
      ? selectedItem.item.patient
      : null;

  const selectedPatientId = selectedPatient?.id ?? null;

  const selectedVisitAddress =
    (selectedItem?.type === selectedItemTypeSchema.enum.plannedVisit ||
      selectedItem?.type === selectedItemTypeSchema.enum.itemToPlan) &&
    selectedPatient &&
    "address" in selectedPatient
      ? selectedPatient.address
      : null;

  const selectedRouteTravelTime = useMemo(() => {
    if (
      !selectedItem ||
      selectedItem.type !== selectedItemTypeSchema.enum.plannedRoute ||
      !directions
    ) {
      return null;
    }
    return directions.legDurations.reduce((acc, curr) => acc + curr, 0);
  }, [directions, selectedItem]);

  const shouldShowTopLeftLabel =
    selectedItem?.type === selectedItemTypeSchema.enum.plannedRoute ||
    Boolean(selectedVisitAddress);

  const patientIdsFromSelectedRoute = useMemo(() => {
    const matchingRoute = timeFilteredRoutes.find(
      (route) => route.id === selectedRouteId,
    );
    return matchingRoute
      ? matchingRoute.visits.map(({ patient }) => patient.id)
      : [];
  }, [timeFilteredRoutes, selectedRouteId]);

  const routeLine = useMemo(() => {
    const emptyCollection = {
      type: "FeatureCollection" as const,
      features: [],
    };
    if (!careHub || !selectedRouteCoordinates.length) {
      return emptyCollection;
    }

    const birdDirections: [number, number][] = [
      [careHub.longitude, careHub.latitude],
      ...selectedRouteCoordinates.map(
        ({ longitude, latitude }) => [longitude, latitude] as [number, number],
      ),
      [careHub.longitude, careHub.latitude],
    ];

    return {
      type: "FeatureCollection" as const,
      features: [
        {
          type: "Feature" as const,
          properties: {
            color: "#2d4ad9", // var(--color-primary-50)
            opacity: 1,
          },
          geometry: {
            type: "LineString" as const,
            coordinates: directions?.coordinates ?? birdDirections,
          },
        },
      ],
    };
  }, [selectedRouteCoordinates, directions, careHub]);

  const routeLabel = useMemo(() => {
    const emptyCollection = {
      type: "FeatureCollection" as const,
      features: [],
    };
    if (!selectedRouteId) {
      return emptyCollection;
    }

    const selectedRoute = timeFilteredRoutes.find(
      ({ id }) => id === selectedRouteId,
    );
    if (!selectedRoute || selectedRoute.visits.length === 0) {
      return emptyCollection;
    }

    const coordinatesWithIndexes = selectedRoute.visits.reduce(
      (acc, visit, index) => {
        if (visit.patient.status === patientStatusSchema.enum.deleted) {
          return acc;
        }
        const {
          patient: {
            address: { coordinates },
            id: patientId,
          },
        } = visit;
        if (acc[patientId]) {
          return {
            ...acc,
            [patientId]: {
              coordinates,
              indexes: [...acc[patientId].indexes, index],
            },
          };
        }
        return { ...acc, [patientId]: { coordinates, indexes: [index] } };
      },
      {} as Record<
        string,
        {
          coordinates: { longitude: number; latitude: number };
          indexes: Array<number>;
        }
      >,
    );

    return {
      type: "FeatureCollection" as const,
      features: Object.values(coordinatesWithIndexes).map(
        ({ coordinates, indexes }) => ({
          type: "Feature" as const,
          properties: {
            name: indexes.map((index) => index + 1).join(","),
            size: 16, //px
          },
          geometry: {
            type: "Point" as const,
            coordinates: [coordinates.longitude, coordinates.latitude],
          },
        }),
      ),
    };
  }, [selectedRouteId, timeFilteredRoutes]);

  const vehiclesData = useMemo(() => {
    return {
      type: "FeatureCollection" as const,
      features:
        vehicles.map((vehicle) => ({
          type: "Feature" as const,
          properties: {
            name: vehicle.licensePlate,
          },
          geometry: {
            type: "Point" as const,
            coordinates: [
              vehicle.location.longitude,
              vehicle.location.latitude,
            ],
          },
        })) ?? [],
    };
  }, [vehicles]);

  const patientsData = useMemo(
    () =>
      buildPatientFeatures({
        patients: filteredPatients ?? [],
        selectedPatientId,
        patientIdsFromSelectedRoute,
      }),
    [filteredPatients, selectedPatientId, patientIdsFromSelectedRoute],
  );

  return (
    <>
      {customErrorMessage ? (
        <ErrorMessage message={customErrorMessage} />
      ) : (
        <> </>
      )}
      {initialAreaToShow ? (
        <Map
          id="planningMap"
          style={{
            height: "100%",
          }}
          initialAreaToShow={initialAreaToShow}
          interactiveLayerIds={[
            patientClusterLayerId,
            patientUnclusteredLayerId,
          ]}
          onClick={handlePatientLayerClick}
          slotTopLeft={
            shouldShowTopLeftLabel ? (
              <div className={styles.topLeftLabel}>
                {selectedItem?.type ===
                selectedItemTypeSchema.enum.plannedRoute ? (
                  <Trans>
                    <span className={styles.bold}>Total restid:</span> ca{" "}
                    {selectedRouteTravelTime} minuter
                  </Trans>
                ) : selectedVisitAddress ? (
                  <Address
                    icon
                    address={{ ...selectedVisitAddress, countryCode }}
                    showPostalCode
                  />
                ) : null}
              </div>
            ) : null
          }
          slotBelowFullscreenControl={
            <UnstyledSingleSelectToggleButtonGroup
              className={styles.mapPositionControl}
              orientation="vertical"
              selectionMode="single"
              disallowEmptySelection
              selectedKey={mapPosition}
              onSelectionChange={setMapPosition}
              aria-label={t`Kartans placering`}
              items={[
                {
                  id: mapPositionSchema.enum.right,
                  content: <PanelRightIcon />,
                  "aria-label": t`Placera kartan till höger`,
                },
                {
                  id: mapPositionSchema.enum.bottom,
                  content: <PanelBottomIcon />,
                  "aria-label": t`Placera kartan nedtill`,
                },
              ]}
            />
          }
        >
          <Source data={routeLine} type="geojson">
            <Layer {...lineLayer} />
          </Source>
          <Source
            id={patientsSourceId}
            data={patientsData}
            type="geojson"
            cluster={true}
            clusterRadius={20}
            clusterProperties={{
              // Coalesce returns the first non-null value, without it clusters would have the value of the last item clustered instead of any item with examined_patient_name set
              examined_patient_name: [
                "coalesce",
                ["get", "examined_patient_name"],
              ],
              // Any returns true if one or more inside the cluster has a home visit
              has_home_visit: ["any", ["get", "has_home_visit"]],
              // Any returns true if one or more inside the cluster is in the selected route
              in_selected_route: ["any", ["get", "in_selected_route"]],
            }}
          >
            <Layer {...clusterLayer("patient")} />
            <Layer {...unclusteredLayer("patient")} />
            <Layer {...circleNameLayer("patient")} />
          </Source>
          <Source
            data={vehiclesData}
            type="geojson"
            cluster={true}
            clusterRadius={20}
          >
            <Layer {...clusterLayer("vehicle")} />
            <Layer {...unclusteredLayer("vehicle")} />
            <Layer {...circleNameLayer("vehicle")} />
          </Source>
          <Source data={routeLabel} type="geojson">
            <Layer {...symbolLayer} />
          </Source>
        </Map>
      ) : null}
    </>
  );
};
